import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationNgRxActions } from '@geekbears/gb-ngrx-auth-basic';
import {
  getFailureMessageRxOp,
  UserModel,
} from '@geekbears/gb-ngrx-stack-base';
import { NotificationsService } from '@geekbears/ngx-gb-ui-kit';
import {
  resetPasswordFailureAction,
  resetPasswordSuccessAction,
  UsersActions,
} from '@modules/company-manager/users/application/store/users.actions';
import { resetPasswordCodeSelector } from '@modules/company-manager/users/application/store/users.selectors';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { APP_ROUTES } from '@typings/app-routes.constant';
import { passwordValidator, getPasswordValidationMessages } from '@utils/password.validator';
import { isNil } from 'lodash';
import { filter, map, Subject, takeUntil, tap } from 'rxjs';

@Component({
  selector: 'app-new-password',
  templateUrl: './new-password.component.html',
  styleUrls: ['./new-password.component.scss'],
})
export class NewPasswordComponent implements OnInit, OnDestroy {
  destroy$: Subject<void> = new Subject();
  form!: FormGroup;
  code: string = '';
  currentUser!: UserModel;
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private notificationsService: NotificationsService,
    private store$: Store,
    private actions$: Actions,
    private activatedRoute: ActivatedRoute,
    private notificationService: NotificationsService
  ) {
    this.form = this.fb.group({
      password: ['', [Validators.required, passwordValidator()]],
      confirmPassword: ['', [Validators.required, passwordValidator()]],
      resetPasswordCode: ['', [Validators.required]],
    });
    this.getAuthenticatedUser();
    this.getAuthenticatedUser();
  }

  getPasswordErrorMessage(controlName: string): string {
    const control = this.form?.get(controlName);
    if (!control || !control.touched || control.valid) {
      return '';
    }
    if (control.errors?.['required']) {
      return 'Please enter your password';
    }
    if (control.errors?.['passwordStrength']?.firstMessage) {
      return control.errors['passwordStrength'].firstMessage;
    }
    const msgs = getPasswordValidationMessages(control.value || '');
    return msgs.length > 0 ? msgs[0] : '';
  }

  getAuthenticatedUser() {
    if (localStorage.getItem('s_user'))
      this.currentUser = JSON.parse(localStorage.getItem('s_user') || '{}');
  }

  ngOnInit(): void {
    this.getParams();
    this.onChangePasswordSuccess();
    this.onFailureActions();
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onChangePasswordSuccess() {
    this.actions$
      .pipe(
        ofType(resetPasswordSuccessAction),
        filter((action) => {
          return !isNil(action.response);
        }),
        map((action: any) => {
          if (action) {
            if (this.currentUser) {
              this.verifyName();
            } else {
              this.router.navigateByUrl('/authentication/');
              this.notificationService.success(
                'Your password have been successfully changed'
              );
            }
            return action.response.data;
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  verifyName() {
    if (
      this.currentUser.firstName == '##N/A##' ||
      this.currentUser.firstName == '##N/A##'
    )
      this.router.navigateByUrl('/authentication/personal-information');
    else {
      // localStorage.clear();
      localStorage.removeItem('s_admin');
      localStorage.removeItem('confirmAccountToken');
      localStorage.removeItem('confirmAccountEmail');
      this.store$.dispatch(AuthenticationNgRxActions.logout());

      this.router.navigateByUrl(APP_ROUTES.authentication['sign-in'].fullPath);
    }
  }

  changePassword() {
    if (this.form.valid) {
      if (
        this.form.get('password')?.value ==
        this.form.get('confirmPassword')?.value
      ) {
        this.store$.dispatch(
          UsersActions.resetPassword({
            resetPasswordData: this.form.value,
          })
        );
      } else {
        this.errorModal('Passwords do not match');
      }
    }
  }
  getResetPasswordCode() {
    this.store$
      .select(resetPasswordCodeSelector)
      .pipe(
        map((response) => {
          if (response) {
            this.form.get('resetPasswordCode')?.setValue(response);
            this.code = response ? response : '';
          } else {
            this.router.navigateByUrl('authentication/forgot-password');
          }
        })
      )
      .subscribe();
  }
  successModal(message: string) {
    this.notificationsService.success(message, {
      duration: 4000,
      icon: 'check-circle',
    });
  }
  errorModal(message: string) {
    this.notificationsService.error(message, {
      duration: 4000,
      icon: 'alert-outline',
    });
  }
  onFailureActions() {
    this.actions$
      .pipe(
        ofType(resetPasswordFailureAction),
        map((action) => {
          return action.failure;
        }),
        getFailureMessageRxOp(),
        tap((errorMessage) => {
          if (errorMessage) {
            this.notificationsService.error(errorMessage, {
              duration: 5000,
              icon: 'alert-outline',
            });
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }
  getParams() {
    this.activatedRoute.params
      .pipe(map((params) => params))
      .subscribe(async (params) => {
        if (params?.['resetPasswordCode'])
          this.form
            .get('resetPasswordCode')
            ?.setValue(params?.['resetPasswordCode']);
      })
      .unsubscribe();
  }
}
